import { DateTime } from 'luxon'
import { CollectDataStepType } from 'src/constants/collect-data/enums'
import { aiReasonCodeTitles } from 'src/constants/document-review/constants'
import type {
  AiDocumentReviewMatchedData,
  AiDocumentReviewReason,
  AiMatchedValueRow,
  ReviewFormValues,
} from 'src/interfaces/documentReview'
import { toFormDateString } from 'src/utils/documentReviewDates'

type MatchedValueFormat = 'text' | 'date' | 'usd'

interface MatchedValueField {
  key: keyof AiDocumentReviewMatchedData
  label: string
  format: MatchedValueFormat
}

/**
 * The ticket's lists, in its order. The effective date listed is the one printed on the document
 * (`documentEffectiveDate`), which is not what "Use these values" writes — that is the
 * prefill-safe `effectiveAt`, absent when the backend would refuse the date — so its label says so.
 */
const matchedValueFieldsByStepType: Partial<Record<CollectDataStepType, MatchedValueField[]>> = {
  [CollectDataStepType.VehicleRegistration]: [
    { key: 'vin', label: 'VIN', format: 'text' },
    { key: 'documentId', label: 'Document ID', format: 'text' },
    { key: 'vehicleMake', label: 'Make', format: 'text' },
    { key: 'vehicleYear', label: 'Year', format: 'text' },
    { key: 'vehicleModel', label: 'Model', format: 'text' },
    { key: 'licensePlateNumber', label: 'License plate', format: 'text' },
    { key: 'stateOfIssue', label: 'State of issue', format: 'text' },
    { key: 'expiresAt', label: 'Expiration date', format: 'date' },
    { key: 'firstName', label: 'Owner first name', format: 'text' },
    { key: 'lastName', label: 'Owner last name', format: 'text' },
    { key: 'documentEffectiveDate', label: 'Effective date (on document)', format: 'date' },
  ],
  [CollectDataStepType.VehicleInsurance]: [
    { key: 'expiresAt', label: 'Expiration date', format: 'date' },
    { key: 'policyNumber', label: 'Policy number', format: 'text' },
    { key: 'documentEffectiveDate', label: 'Effective date (on document)', format: 'date' },
    { key: 'coverageAmount', label: 'Coverage amount', format: 'usd' },
    { key: 'vin', label: 'VIN', format: 'text' },
    { key: 'vehicleMake', label: 'Make', format: 'text' },
    { key: 'vehicleYear', label: 'Year', format: 'text' },
    { key: 'vehicleModel', label: 'Model', format: 'text' },
    { key: 'firstName', label: 'Policy holder first name', format: 'text' },
    { key: 'lastName', label: 'Policy holder last name', format: 'text' },
  ],
}

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

/** Treats null, empty and whitespace-only matches as absent — they are neither shown nor prefilled. */
const toPresentString = (value: unknown): string | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : undefined
  }

  if (typeof value !== 'string') {
    return undefined
  }

  const trimmed = value.trim()

  return trimmed === '' ? undefined : trimmed
}

const formatMatchedValue = (value: unknown, format: MatchedValueFormat): string | undefined => {
  if (format === 'usd' && typeof value === 'number' && Number.isFinite(value)) {
    return usdFormatter.format(value)
  }

  const presentValue = toPresentString(value)

  if (presentValue === undefined || format !== 'date') {
    return presentValue
  }

  const parsed = DateTime.fromISO(presentValue, { zone: 'utc' })

  return parsed.isValid ? parsed.toFormat('MM/dd/yyyy') : presentValue
}

/** The readable title for a decision reason code, or the raw code when it has none. */
export const resolveAiReasonTitle = (code: string): string => {
  if (!Object.prototype.hasOwnProperty.call(aiReasonCodeTitles, code)) {
    return code
  }

  return aiReasonCodeTitles[code as keyof typeof aiReasonCodeTitles]
}

/** Warning and blocking reasons show Expected / Provided whenever either value exists. */
export const hasReasonComparison = (reason: AiDocumentReviewReason): boolean => {
  if (reason.severity === 'info') {
    return false
  }

  return (
    toPresentString(reason.expected) !== undefined || toPresentString(reason.actual) !== undefined
  )
}

/** An Expected / Provided value as shown, with a dash for a missing or blank one. */
export const formatReasonComparisonValue = (value: string | undefined): string => {
  return toPresentString(value) ?? '—'
}

/** The AI's matched values to show for this step type, skipping null and empty ones. */
export const buildAiMatchedValueRows = (
  stepType: CollectDataStepType,
  matchedData: AiDocumentReviewMatchedData | undefined,
): AiMatchedValueRow[] => {
  const fields = matchedValueFieldsByStepType[stepType]

  if (!matchedData || !fields) {
    return []
  }

  return fields.reduce<AiMatchedValueRow[]>((rows, field) => {
    const value = formatMatchedValue(matchedData[field.key], field.format)

    if (value === undefined) {
      return rows
    }

    return [...rows, { label: field.label, value }]
  }, [])
}

interface PrefillInput {
  stepType: CollectDataStepType
  matchedData: AiDocumentReviewMatchedData
  values: ReviewFormValues
}

/**
 * "Use these values": writes the AI's matched values over the form, keeping the previous value
 * wherever a match is null, empty or (for dates) does not parse. The caller gates this on
 * `prefillSafe` — this function never decides whether prefilling is allowed.
 *
 * On `vehicle_and_insurance` the Document ID is the policy number. Dates are normalised to the
 * `'yyyy-MM-dd'` strings the form's date fields hold. Only the prefill-safe `effectiveAt` is used,
 * never `documentEffectiveDate` — that is the date printed on the page, and submitting it as the
 * approval's effective date is an approval the backend refuses. The address is never touched.
 */
export const applyAiMatchedDataPrefill = ({
  stepType,
  matchedData,
  values,
}: PrefillInput): ReviewFormValues => {
  const matchedDocumentId =
    stepType === CollectDataStepType.VehicleInsurance
      ? matchedData.policyNumber
      : matchedData.documentId

  return {
    ...values,
    firstName: toPresentString(matchedData.firstName) ?? values.firstName,
    lastName: toPresentString(matchedData.lastName) ?? values.lastName,
    documentId: toPresentString(matchedDocumentId) ?? values.documentId,
    dateIssued: toFormDateString(toPresentString(matchedData.dateIssued)) ?? values.dateIssued,
    expiresAt: toFormDateString(toPresentString(matchedData.expiresAt)) ?? values.expiresAt,
    effectiveAt: toFormDateString(toPresentString(matchedData.effectiveAt)) ?? values.effectiveAt,
  }
}
